import { useTranslations } from '@aikoa/i18n/react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useLocalSearchParams } from 'expo-router'
import { useMemo, useState } from 'react'
import { View } from 'react-native'
import { OrganisationFormFields } from '~/components/admin/OrganisationFormFields'
import { OrganisationPreview } from '~/components/admin/OrganisationPreview'
import { useAlert } from '~/components/GlobalAlert'
import Icon from '~/components/Icon'
import ContentBox from '~/components/layout/ContentBox'
import ScreenView from '~/components/layout/ScreenView'
import { Body, Label } from '~/components/typography/Content'
import { Heading2 } from '~/components/typography/Titles'
import { Button, ButtonLabel } from '~/components/ui/button'
import { Text } from '~/components/ui/text'
import RealtorProfileForm, {
  type RealtorProfileFormValues,
} from '~/components/user/realtors/RealtorProfileForm'
import { useOrganisationByDomain } from '~/data/admin/useOrganisationByDomain'
import {
  formValuesToCreatePayload,
  useCreateUserWithProfile,
} from '~/data/user/createUserWithProfile'
import { trpc } from '~/lib/trpc'
import { cn } from '~/lib/utils'

export default function AddUser() {
  const { t } = useTranslations(['admin', 'common'])
  const { showAlert } = useAlert()
  const { inviteId: inviteIdParam } = useLocalSearchParams<{ inviteId?: string }>()
  const inviteId = typeof inviteIdParam === 'string' ? inviteIdParam : undefined
  const [formValues, setFormValues] = useState<RealtorProfileFormValues | null>(null)
  const [isFormValid, setIsFormValid] = useState(false)
  const [createdUserId, setCreatedUserId] = useState<string | null>(null)
  const [createdPhoneNumber, setCreatedPhoneNumber] = useState<string | null>(null)
  const [organisationName, setOrganisationName] = useState('')

  const createUserWithProfileMutation = useCreateUserWithProfile()
  const sendWelcomeEmailMutation = useMutation(trpc.admin.sendWelcomeEmail.mutationOptions())
  const { data: invitation } = useQuery(
    trpc.admin.getUserInvitation.queryOptions(
      { invitationId: inviteId ?? '' },
      { enabled: !!inviteId, staleTime: 60_000 }
    )
  )

  const emailDomain = useMemo(() => {
    const email = formValues?.email
    if (!email || !email.includes('@')) {
      return null
    }
    return email.split('@')[1]?.toLowerCase() || null
  }, [formValues?.email])

  const { data: existingOrganisation, isLoading: isLoadingOrganisation } =
    useOrganisationByDomain(emailDomain)

  const initialFormValues = useMemo<Partial<RealtorProfileFormValues>>(() => {
    if (!invitation) {
      return {}
    }

    return {
      email: invitation.email,
      phoneNumber: invitation.phone ?? '',
    }
  }, [invitation])

  const shouldShowOrganisationForm =
    !!emailDomain && !existingOrganisation && !isLoadingOrganisation

  const shouldShowOrganisationPreview = !!existingOrganisation

  const handleValuesChange = (values: RealtorProfileFormValues, isValid: boolean) => {
    setFormValues(values)
    setIsFormValid(isValid)
  }

  const handleSubmit = () => {
    if (!formValues || !isFormValid) {
      showAlert({
        type: 'error',
        title: t(($) => $.common.error.error),
        description: t(($) => $.createUser.error.fillAllRequired),
      })
      return
    }

    const payload = formValuesToCreatePayload(formValues)

    if (!payload.name || (!payload.phoneNumber && !payload.email)) {
      showAlert({
        type: 'error',
        title: t(($) => $.common.error.error),
        description: t(($) => $.createUser.error.nameAndContactRequired),
      })
      return
    }

    if (shouldShowOrganisationForm && !organisationName.trim()) {
      showAlert({
        type: 'error',
        title: t(($) => $.common.error.error),
        description: t(($) => $.createUser.organisation.nameRequired),
      })
      return
    }

    const mutationPayload = {
      ...payload,
      inviteId,
      createOrganisation: shouldShowOrganisationForm
        ? {
            name: organisationName.trim(),
          }
        : undefined,
    }

    createUserWithProfileMutation.mutate(mutationPayload, {
      onSuccess: (data) => {
        setCreatedUserId(data.userId)
        setCreatedPhoneNumber(data.phoneNumber || null)
        const description = data.phoneNumber
          ? t(($) => $.createUser.successDescriptionWithPhone, {
              phoneNumber: data.phoneNumber,
            })
          : data.email
            ? t(($) => $.createUser.successDescriptionWithEmail, {
                email: data.email,
              })
            : t(($) => $.createUser.successDescriptionGeneric)
        showAlert({
          type: 'success',
          title: t(($) => $.createUser.successTitle),
          description,
        })
      },
      onError: (error) => {
        showAlert({
          type: 'error',
          title: t(($) => $.common.error.error),
          description:
            error instanceof Error ? error.message : t(($) => $.createUser.error.createFailed),
        })
      },
    })
  }

  const handleSendWelcomeEmail = () => {
    if (!createdUserId) {
      return
    }

    sendWelcomeEmailMutation.mutate(
      { userId: createdUserId },
      {
        onSuccess: () => {
          showAlert({
            type: 'success',
            title: t(($) => $.createUser.sendWelcomeEmailSuccessTitle),
            description: t(($) => $.createUser.sendWelcomeEmailSuccessDescription),
          })
        },
        onError: (error) => {
          showAlert({
            type: 'error',
            title: t(($) => $.common.error.error),
            description:
              error instanceof Error
                ? error.message
                : t(($) => $.createUser.sendWelcomeEmailErrorDescription),
          })
        },
      }
    )
  }

  if (createdUserId && (createdPhoneNumber || formValues?.email)) {
    const contactInfo = createdPhoneNumber || formValues?.email || ''

    return (
      <ScreenView>
        <ContentBox
          className="flex-1 justify-center"
          contentContainerClassName="gap-8"
          variant="transparent">
          <View className="items-center gap-6">
            <View className="max-w-md gap-4">
              <Heading2 className="text-center">{t(($) => $.createUser.successTitle)}</Heading2>

              <Body className="text-pretty text-center text-lg/normal">
                {t(($) => $.createUser.successDescription)}{' '}
                {createdPhoneNumber
                  ? t(($) => $.createUser.withPhone)
                  : t(($) => $.createUser.withEmail)}
                :
              </Body>

              <View className="rounded-xl bg-neutral-100 p-4">
                <Body className="text-center font-semibold text-xl">{contactInfo}</Body>
              </View>

              <View className="gap-3">
                <Button
                  variant="outline"
                  size="lg"
                  onPress={handleSendWelcomeEmail}
                  loading={sendWelcomeEmailMutation.isPending}>
                  <ButtonLabel>{t(($) => $.createUser.sendWelcomeEmailButton)}</ButtonLabel>
                </Button>

                <Button
                  variant="primary"
                  size="lg"
                  onPress={() => {
                    setCreatedUserId(null)
                    setCreatedPhoneNumber(null)
                    setFormValues(null)
                    setIsFormValid(false)
                    setOrganisationName('')
                  }}>
                  <ButtonLabel>{t(($) => $.createUser.buttonNew)}</ButtonLabel>
                </Button>
              </View>
            </View>
          </View>
        </ContentBox>
      </ScreenView>
    )
  }

  return (
    <ScreenView>
      <ContentBox className="flex-1" contentContainerClassName="gap-6 px-0" variant="transparent">
        <View className="gap-4">
          <Heading2 className="text-center">{t(($) => $.createUser.title)}</Heading2>
          <Body className="text-pretty text-center text-neutral-700">
            {t(($) => $.createUser.description)}
          </Body>
        </View>

        <RealtorProfileForm
          initialValues={initialFormValues}
          onValuesChange={handleValuesChange}
          showCompletionStatus={false}
          showAvatarUpload
          useDirectContactInputs
          className="flex-1"
        />

        {shouldShowOrganisationPreview && existingOrganisation && (
          <View className="px-6">
            <OrganisationPreview organisation={existingOrganisation} />
          </View>
        )}

        {shouldShowOrganisationForm && (
          <View className="gap-4 px-6">
            <View className="gap-2">
              <Label className="font-semibold text-base">
                {t(($) => $.createUser.organisation.title)}
              </Label>
              <Body className="text-sm text-secondary">
                {t(($) => $.createUser.organisation.description)}
              </Body>
            </View>
            <OrganisationFormFields
              name={organisationName}
              onNameChange={setOrganisationName}
              emailDomain={emailDomain || undefined}
              showDomainField={false}
            />
          </View>
        )}

        <View className="gap-4 px-6 pb-6">
          {!isFormValid && formValues && (
            <View className="gap-2">
              <View
                className={cn(
                  'mx-auto w-auto max-w-md flex-row items-center gap-3 rounded-xl bg-neutral-400/70 px-6 py-3 shadow-sm shadow-tertiary-foreground'
                )}>
                <Icon name="alert-circle-outline" size={16} className="text-neutral-800" />
                <Text className="text-neutral-800">
                  {t(($) => $.createUser.fillRequiredFields)}
                </Text>
              </View>
            </View>
          )}

          <Button
            variant="primary"
            size="lg"
            disabled={!isFormValid}
            onPress={handleSubmit}
            loading={createUserWithProfileMutation.isPending}>
            <ButtonLabel>{t(($) => $.createUser.button)}</ButtonLabel>
          </Button>
        </View>
      </ContentBox>
    </ScreenView>
  )
}
